import { useState, useEffect, useCallback } from "react";
import { useAtom } from "jotai";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { v4 as uuidv4 } from "uuid";
import { debounce } from "lodash";
import { motion } from "framer-motion";

// MUI Components
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Snackbar from "@mui/material/Snackbar";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import Tooltip from "@mui/material/Tooltip";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import Modal from "@mui/material/Modal";

// MUI Icons
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PeopleIcon from "@mui/icons-material/People";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import RemoveIcon from "@mui/icons-material/Remove";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

// Import sub-components
import ClientTabBreakdownItem from "../../../ClientTabBreakdownItem";
import FileSizes from "../../../Event Data Modal Components/FileSizes";
import Contacts from "../../../Event Data Modal Components/Contacts";
import Notes from "../../../Event Data Modal Components/Client Tab Components/Notes";
import Invoicing from "../../../Event Data Modal Components/Client Tab Components/Invoicing";
import Checklists from "../../../Event Data Modal Components/Client Tab Components/Checklists";
import Files from "../../../Event Data Modal Components/Client Tab Components/Files";
import UniversalTextField from "../../../../GlobalComponents/UniversalTextField";
import ChangeClientNameDialog from "../../../../GlobalComponents/ChangeClientNameDialog";

// Import atoms
import { themeAtom, isAdminAtom } from "../../../../-Atoms";

// Firebase imports
import {
  db,
  updateDoc,
  doc,
  storage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  setDoc,
} from "../../../../firebaseConfig";

// Dropbox configuration
const Dropbox = require("dropbox").Dropbox;
const dbx = new Dropbox({
  accessToken:
    "b-TxMhg7wmcAAAAAABiOl_5JiyeefA0elglApZxmxg3Uc9n7uXMt65Um5ajpJjuC",
});

/**
 * ClientTab Component for EventDataModalV2
 */
const ClientTab = ({ jobClientData, setHasUnsavedChanges }) => {
  // State
  const [clientData, setClientData] = useState(jobClientData);
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [canEdit, setCanEdit] = useState(isAdmin);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  const updateFS = useCallback(
    debounce(async (id, field, value) => {
      if (!id) return;
      try {
        await setDoc(
          doc(db, "ClientDatabase", id),
          { [field]: value },
          { merge: true }
        );
      } catch (error) {
        console.error("Error updating document: ", error);
      }
    }, 800),
    []
  );

  // Logo upload state
  const [logoUploadOpen, setLogoUploadOpen] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [changeNameOpen, setChangeNameOpen] = useState(false);

  // Client details state
  const [PSF, setPSF] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [turnAround, setTurnAround] = useState("");
  const [isDigitalBackground, setIsDigitalBackground] = useState(
    clientData?.isDigitalBackground || false
  );

  // Payment terms mapping for display/database values
  const paymentTermsOptions = [
    { label: "COD", value: "cod" },
    { label: "Payment Terms", value: "payment_terms" },
  ];

  // Helper function to normalize payment terms value
  const normalizePaymentTerms = (value) => {
    if (!value) return "payment_terms";
    if (value.toLowerCase() === "cod") return "cod";
    return "payment_terms";
  };

  // Client breakdown state (Image Breakdown)
  const [imageBreakdown, setImageBreakdown] = useState(
    clientData?.imageBreakdown || []
  );

  // Spreadsheet state
  const [spreadsheetURL, setSpreadsheetURL] = useState(
    clientData?.spreadsheetURL
  );
  const [noClick, setNoClick] = useState(false);

  // Reorder dialog state
  const [openReorderDialog, setOpenReorderDialog] = useState(false);
  const [reorderedImageBreakdown, setReorderedImageBreakdown] = useState(
    imageBreakdown || []
  );
  const [showRemoveBreakdownItem, setShowRemoveBreakdownItem] = useState(false);

  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [suppliedLinks, setSuppliedLinks] = useState([]);

  // Effects
  useEffect(() => {
    if (jobClientData?.id) {
      setClientData(jobClientData);
    }
  }, [jobClientData]);

  useEffect(() => {
    setPSF(clientData?.PSF || "");
    setPaymentTerms(clientData?.paymentTerms || "");
    setTurnAround(clientData?.turnAround || "");
    setIsDigitalBackground(clientData?.isDigitalBackground || false);
    setImageBreakdown(clientData?.imageBreakdown || []);
    setSpreadsheetURL(clientData?.spreadsheetURL || "");
  }, [clientData]);

  useEffect(() => {
    setReorderedImageBreakdown(imageBreakdown || []);
  }, [imageBreakdown]);

  // Payment terms normalization effect
  useEffect(() => {
    if (clientData?.id && clientData?.paymentTerms !== undefined) {
      const currentValue = clientData?.paymentTerms;
      const normalizedValue = normalizePaymentTerms(currentValue);

      if (currentValue !== normalizedValue) {
        updateFS(clientData.id, "paymentTerms", normalizedValue);
      }

      setPaymentTerms(normalizedValue);
    }
  }, [clientData?.paymentTerms, clientData?.id, updateFS]);

  useEffect(() => {
    setCanEdit(isAdmin);
  }, [isAdmin]);

  useEffect(() => {
    if (clientData?.attachments) {
      setAttachments([...clientData?.attachments]);
    } else {
      setAttachments([]);
    }

    if (clientData?.suppliedLinks) {
      const reverse = clientData?.suppliedLinks.reverse();
      setSuppliedLinks(reverse);
    }
  }, [clientData]);

  // Handlers
  const handleLogoUpload = async () => {
    if (!logoFile) return;

    setLogoUploading(true);

    try {
      const logoRef = storageRef(
        storage,
        `client-logos/${clientData?.id}/${logoFile.name}`
      );

      await uploadBytes(logoRef, logoFile);

      const logoUrl = await getDownloadURL(logoRef);

      updateFS(clientData?.id, "logo", logoUrl);

      setLogoUploadOpen(false);
      setLogoFile(null);
    } catch (error) {
      console.error("Error uploading logo:", error);
    } finally {
      setLogoUploading(false);
    }
  };

  const addNewBreakdownItem = () => {
    if (!canEdit) return;
    const newitemObj = {
      id: uuidv4(),
      description: "",
      jobTypesFilter: [],
      expectedPerHour: "",
      costPerFile: "",
      existingItem: true,
    };
    const newBreakdown = [...imageBreakdown, newitemObj];
    setImageBreakdown(newBreakdown);
    updateDoc(doc(db, "ClientDatabase", clientData?.id), {
      imageBreakdown: newBreakdown,
    }).catch((e) => console.log(e));
  };

  const toggleRemoveBreakdownItem = () => {
    setShowRemoveBreakdownItem(true);
    setTimeout(() => {
      setShowRemoveBreakdownItem(false);
    }, 6000);
  };

  const handleReorderClick = () => {
    setOpenReorderDialog(true);
  };

  const handleCloseReorderDialog = () => {
    setOpenReorderDialog(false);
  };

  const handleSaveReorderedImageBreakdown = () => {
    if (!canEdit) return;
    setImageBreakdown(reorderedImageBreakdown);
    updateDoc(doc(db, "ClientDatabase", clientData?.id), {
      imageBreakdown: reorderedImageBreakdown,
    }).catch((e) => console.log(e));
    setOpenReorderDialog(false);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(reorderedImageBreakdown);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setReorderedImageBreakdown(items);
  };

  const copyToClipboard = () => {
    if (spreadsheetURL) {
      navigator.clipboard.writeText(spreadsheetURL);
      setCopySnackbarOpen(true);
    }
  };

  // Render
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {clientData?.id ? (
        <Box sx={{ width: "100%", position: "relative" }}>
          {/* Header Section - Logo, Brand, Buttons */}
          <Box
            sx={{
              width: "100%",
              borderRadius: "10px",
              overflow: "hidden",
              backgroundColor:
                theme === "dark"
                  ? "rgba(35,35,35,0.7)"
                  : "rgba(235,235,235,0.9)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow:
                theme === "dark"
                  ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                  : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
              padding: 3,
              mb: 2,
            }}
          >
            {clientData?.logo && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 2,
                  cursor: "pointer",
                }}
                onClick={() => setLogoUploadOpen(true)}
              >
                <img
                  src={clientData.logo}
                  alt={`${clientData?.brand} logo`}
                  style={{
                    maxHeight: "100px",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}

            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                textAlign: "center",
                mb: 2,
                color:
                  theme === "dark"
                    ? "rgba(255,255,255,0.9)"
                    : "rgba(0,0,0,0.9)",
              }}
            >
              {clientData?.brand || "Client"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloudUploadIcon />}
                onClick={() => setLogoUploadOpen(true)}
                disabled={!canEdit}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  borderColor:
                    theme === "dark"
                      ? "rgba(255,255,255,0.23)"
                      : "rgba(0,0,0,0.23)",
                  "&:hover": {
                    borderColor: theme === "dark" ? "#90caf9" : "#1976d2",
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(144, 202, 249, 0.08)"
                        : "rgba(25, 118, 210, 0.08)",
                  },
                }}
              >
                {clientData?.logo ? "Change Logo" : "Add Logo"}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setChangeNameOpen(true)}
                disabled={!canEdit || !clientData?.id}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                }}
              >
                Change Name
              </Button>
            </Box>
          </Box>

          {/* Spreadsheet Section */}
          <Box
            sx={{
              width: "100%",
              borderRadius: "10px",
              overflow: "hidden",
              backgroundColor:
                theme === "dark"
                  ? "rgba(35,35,35,0.7)"
                  : "rgba(235,235,235,0.9)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow:
                theme === "dark"
                  ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                  : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
              padding: 3,
              mb: 2,
            }}
          >
            {!spreadsheetURL ? (
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  startIcon={
                    noClick ? <CircularProgress size={20} /> : <AddIcon />
                  }
                  disabled={noClick || !canEdit}
                  onClick={async () => {
                    if (!canEdit) return;
                    if (!noClick) {
                      setNoClick(true);
                      const res = await fetch(
                        "https://createclientspreadsheet-y5ybiyz7ga-uc.a.run.app",
                        {
                          method: "POST",
                          headers: {
                            "content-type": "application/json",
                          },
                          body: JSON.stringify({
                            brand: clientData?.brand,
                            id: clientData?.id,
                          }),
                        }
                      )
                        .then((res) => {
                          return res.json();
                        })
                        .catch(() => {
                          alert("an error occurred");
                        });
                      setSpreadsheetURL(res.url);
                      updateFS(clientData?.id, "spreadsheetURL", res.url);
                      setNoClick(false);
                    }
                  }}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                  }}
                >
                  {noClick ? "Creating..." : "Create Client Spreadsheet"}
                </Button>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<PeopleIcon />}
                    disabled={noClick || !canEdit}
                    onClick={async () => {
                      if (!canEdit) return;
                      if (!noClick) {
                        setNoClick(true);
                        const spreadsheetId =
                          spreadsheetURL.match(/\/d\/(.+)\//)[1];
                        const clientEmails = [];
                        clientData?.contacts.forEach((c) => {
                          if (c.email) {
                            clientEmails.push(c.email);
                          }
                        });
                        await fetch(
                          "https://sharespreadsheettoclients-y5ybiyz7ga-uc.a.run.app",
                          {
                            method: "POST",
                            headers: {
                              "content-type": "application/json",
                            },
                            body: JSON.stringify({
                              emails: clientEmails,
                              id: spreadsheetId,
                            }),
                          }
                        )
                          .then((res) => {
                            return res.json();
                          })
                          .catch(() => {
                            alert("an error occurred");
                          });
                        setNoClick(false);
                      }
                    }}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                    }}
                  >
                    Share With Clients
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    justifyContent: "center",
                    mt: 2,
                  }}
                >
                  <Tooltip title="Copy spreadsheet URL to clipboard">
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={copyToClipboard}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                      }}
                    >
                      Copy URL
                    </Button>
                  </Tooltip>
                  <Tooltip title="Open spreadsheet in new tab">
                    <Button
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => window.open(spreadsheetURL, "_blank")}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                      }}
                    >
                      Open Spreadsheet
                    </Button>
                  </Tooltip>
                  <Tooltip title="Reset spreadsheet URL">
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<RestartAltIcon />}
                      onClick={() => {
                        if (!canEdit) return;
                        if (
                          window.confirm(
                            "Are you sure you want to reset the spreadsheet URL? This cannot be undone."
                          )
                        ) {
                          setSpreadsheetURL("");
                          updateFS(clientData?.id, "spreadsheetURL", "");
                        }
                      }}
                      disabled={!canEdit}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                      }}
                    >
                      Reset
                    </Button>
                  </Tooltip>
                </Box>
              </>
            )}
          </Box>

          {/* Contacts Section */}
          <Box sx={{ mb: 2 }}>
            <Contacts client={clientData} />
          </Box>

          {/* PSF / Payment Terms / Turn Around Section */}
          <Box
            sx={{
              width: "100%",
              borderRadius: "10px",
              overflow: "hidden",
              backgroundColor:
                theme === "dark"
                  ? "rgba(35,35,35,0.7)"
                  : "rgba(235,235,235,0.9)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow:
                theme === "dark"
                  ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                  : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
              padding: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <UniversalTextField
                  collectionName="ClientDatabase"
                  docId={clientData?.id}
                  fieldName="PSF"
                  value={PSF || ""}
                  label="PSF"
                  variant="filled"
                  disabled={!canEdit}
                  adminOnly={false}
                  noPadding
                  sx={{
                    marginTop: 0,
                    "& .MuiInputBase-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                  inputProps={{ style: { fontSize: "0.875rem" } }}
                />
              </Box>
              <FormControl
                variant="filled"
                size="small"
                disabled={!canEdit}
                sx={{ flex: 1 }}
              >
                <InputLabel>Payment Terms</InputLabel>
                <Select
                  value={paymentTerms || "payment_terms"}
                  onChange={(e) => {
                    setPaymentTerms(e.target.value);
                    updateFS(clientData?.id, "paymentTerms", e.target.value);
                  }}
                  disableUnderline
                >
                  {paymentTermsOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ flex: 1 }}>
                <UniversalTextField
                  collectionName="ClientDatabase"
                  docId={clientData?.id}
                  fieldName="turnAround"
                  value={turnAround || ""}
                  label="Turn Around"
                  variant="filled"
                  disabled={!canEdit}
                  adminOnly={false}
                  noPadding
                  sx={{
                    marginTop: 0,
                    "& .MuiInputBase-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                  inputProps={{ style: { fontSize: "0.875rem" } }}
                />
              </Box>
            </Box>
          </Box>

          {/* Invoicing Section */}
          <Box sx={{ mb: 2 }}>
            <Invoicing
              client={clientData}
              updateFS={updateFS}
              canEdit={canEdit}
              theme={theme}
            />
          </Box>

          {/* Image Breakdown Section */}
          <Box
            sx={{
              width: "100%",
              borderRadius: "10px",
              overflow: "hidden",
              backgroundColor:
                theme === "dark"
                  ? "rgba(35,35,35,0.7)"
                  : "rgba(235,235,235,0.9)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow:
                theme === "dark"
                  ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                  : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
              padding: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.9)"
                      : "rgba(0,0,0,0.9)",
                }}
              >
                Image Breakdown
              </Typography>
              <Box>
                <IconButton onClick={handleReorderClick} size="small">
                  <SwapVertIcon />
                </IconButton>
                <IconButton onClick={toggleRemoveBreakdownItem} size="small">
                  <RemoveIcon />
                </IconButton>
                <IconButton onClick={addNewBreakdownItem} size="small">
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {imageBreakdown &&
                imageBreakdown.map((bi, index) => (
                  <ClientTabBreakdownItem
                    key={bi.id}
                    item={bi}
                    items={imageBreakdown}
                    setItems={setImageBreakdown}
                    index={index}
                    showRemoveBreakdownItem={showRemoveBreakdownItem}
                    clientData={clientData}
                    updateFS={updateFS}
                  />
                ))}
            </Box>
          </Box>

          {/* Digital Background & No Retouching Section */}
          <Box
            sx={{
              width: "100%",
              borderRadius: "10px",
              overflow: "hidden",
              backgroundColor:
                theme === "dark"
                  ? "rgba(35,35,35,0.7)"
                  : "rgba(235,235,235,0.9)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow:
                theme === "dark"
                  ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                  : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
              padding: 2,
              mb: 2,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <FormGroup row={true} sx={{ gap: 5 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isDigitalBackground}
                    onChange={(e) => {
                      setIsDigitalBackground(e.target.checked);
                      updateDoc(doc(db, "ClientDatabase", clientData?.id), {
                        isDigitalBackground: e.target.checked,
                      });
                    }}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: theme === "dark" ? "#90caf9" : "#1976d2",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor:
                            theme === "dark" ? "#90caf9" : "#1976d2",
                        },
                    }}
                  />
                }
                label={
                  isDigitalBackground
                    ? "Digital Background"
                    : "Organic Background"
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={clientData?.noRetouching}
                    onChange={(e) => {
                      updateDoc(doc(db, "ClientDatabase", clientData?.id), {
                        noRetouching: e.target.checked,
                      });
                    }}
                  />
                }
                label="No Retouching"
              />
            </FormGroup>
          </Box>

          {/* FileSizes Section */}
          <Box sx={{ mb: 2 }}>
            <FileSizes
              client={clientData}
              updateFS={updateFS}
              canEdit={canEdit}
              theme={theme}
            />
          </Box>

          {/* Notes Section */}
          <Box sx={{ mb: 2 }}>
            <Notes
              client={clientData}
              updateFS={updateFS}
              canEdit={canEdit}
              theme={theme}
            />
          </Box>

          {/* Files Section */}
          <Box sx={{ mb: 2 }}>
            <Files
              client={clientData}
              updateFS={updateFS}
              attachments={attachments}
              setAttachments={setAttachments}
              dbx={dbx}
              suppliedLinks={suppliedLinks}
              canEdit={canEdit}
              theme={theme}
            />
          </Box>

          {/* Checklists Section */}
          <Box sx={{ mb: 2 }}>
            <Checklists
              client={clientData}
              updateFS={updateFS}
              canEdit={canEdit}
            />
          </Box>

          {/* Reorder Dialog */}
          <Dialog open={openReorderDialog} onClose={handleCloseReorderDialog}>
            <DialogTitle>Reorder Image Breakdown Items</DialogTitle>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="imageBreakdownList">
                {(provided) => (
                  <List {...provided.droppableProps} ref={provided.innerRef}>
                    {reorderedImageBreakdown.map((item, index) => (
                      <Draggable
                        key={item.id}
                        draggableId={item.id}
                        index={index}
                      >
                        {(provided) => (
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              background:
                                theme === "dark" ? "#363636" : "#f8f8f8",
                              borderBottom: "1px solid grey",
                              marginBottom: 1,
                            }}
                          >
                            <DragHandleIcon sx={{ marginRight: 2 }} />
                            <ListItemText
                              primary={item.description || "Untitled"}
                            />
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
            <DialogActions>
              <Button onClick={handleCloseReorderDialog}>Cancel</Button>
              <Button onClick={handleSaveReorderedImageBreakdown}>Save</Button>
            </DialogActions>
          </Dialog>

          {/* Logo Upload Modal */}
          <Modal
            open={logoUploadOpen}
            onClose={() => !logoUploading && setLogoUploadOpen(false)}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: isMobile ? "85%" : 400,
                maxWidth: 400,
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                Upload Client Logo
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 2 }}
                  disabled={logoUploading}
                >
                  Select Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => setLogoFile(e.target.files[0])}
                  />
                </Button>

                {logoFile && (
                  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    Selected: {logoFile.name}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  onClick={() => setLogoUploadOpen(false)}
                  disabled={logoUploading}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleLogoUpload}
                  disabled={!logoFile || logoUploading}
                  startIcon={
                    logoUploading ? <CircularProgress size={16} /> : null
                  }
                >
                  {logoUploading ? "Uploading..." : "Upload"}
                </Button>
              </Box>
            </Box>
          </Modal>

          <ChangeClientNameDialog
            open={changeNameOpen}
            onClose={() => setChangeNameOpen(false)}
            clientId={clientData?.id}
            currentName={clientData?.brand}
          />

          {/* Copy Snackbar */}
          <Snackbar
            open={copySnackbarOpen}
            autoHideDuration={3000}
            onClose={() => setCopySnackbarOpen(false)}
            message="URL copied to clipboard"
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            borderRadius: "10px",
            backgroundColor:
              theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
            boxShadow:
              theme === "dark"
                ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color:
                theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
            }}
          >
            No client selected
          </Typography>
        </Box>
      )}
    </motion.div>
  );
};

export default ClientTab;
